'use client'

import { useState, useMemo, useCallback, useEffect } from 'react'
import {
  Pmp_Param_Types_Enum,
  TokenDetailsFragment,
  Pmp_Configs,
  PmpConfigDetailsFragment,
  PmpDetailsFragment
} from '@/gql/generated/graphql'
import { cn } from '@/lib/utils'
import { useNavBar } from '@components/nav/nav-provider'
import {
  BodyText,
  HeadlineText,
  DetailText,
  LeftArrowIcon,
  LockIcon,
  InfoCircleIcon,
  LeftCaretIcon,
  DateTimePicker,
  formatDateForPicker,
  convertDateValueToString,
  type DateValue
} from 'design-configuration/components'
import { Input } from '@components/input'
import { Button } from '@components/button'
import { ColorPicker } from '@components/color-picker'
import { useAuthenticatedWalletClient } from '@/hooks/use-authenticated-wallet-client'
import { trackButtonClicked, TrackedButtonNames } from '@utils/analytics'
import { useWriteContract, useWaitForTransactionReceipt } from 'wagmi'
import { IPMPV0ABI } from '@/abi/IPMPV0'
import { LoadingSpinner } from '@components/loading-spinner'
import { pollPmpsMetadata } from '@utils/poll-pmps-metadata'
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from 'design-configuration/components'
import { PmpCustomizableDetails } from './pmp-customizable-details'
import { z } from 'zod'
import { formatConfiguredPMPValue, BIT_255, BIT_256, formatPmpTokenParamsInput } from '../utils'
import SwitchToggle from '@components/switch-toggle'
import { useRouter } from 'next/navigation'
import { usePmpConfigWithOverride } from '../_hooks/usePmpConfigWithOverride'
import { GAS_WARS_CORE_CONTRACT_ADDRESS, GAS_WARS_PROJECT_ID } from '@consts/common'

// These projects use post params but do not have collector configurable fields, hide from the UI
export const PROJECTS_WITHOUT_COLLECTOR_CONFIGURABLE_FIELDS = [
  `${GAS_WARS_CORE_CONTRACT_ADDRESS}-${GAS_WARS_PROJECT_ID}`
]

type GridSelectorOption = {
  value: string
  label: string
}

const GridSelector = ({
  options,
  selected,
  setSelected
}: {
  options: GridSelectorOption[]
  selected: string
  setSelected: (option: string) => void
}): React.ReactElement => {
  const isSelected = (option: GridSelectorOption) => selected === option.value

  return (
    <div className="gap-xs grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => setSelected(option.value)}
          className={cn(
            'px-sm py-xs rounded-xs hover:bg-primary-inverted-10 hover:border-outline-inverted hover:text-on-surface-inverted border text-center transition-all duration-200',
            isSelected(option)
              ? 'bg-primary-inverted text-on-primary-inverted border-outline-inverted'
              : 'bg-surface-inverted text-on-surface-inverted border-outline-inverted-subtle'
          )}
        >
          <BodyText>{option.label}</BodyText>
        </button>
      ))}
    </div>
  )
}

type PmpFieldProps = {
  config: PmpConfigDetailsFragment
  activePmp?: PmpDetailsFragment
  authorizedAddresses: string[]
  connectedUserAddress?: string
  token: TokenDetailsFragment
  customizationParams: Record<string, string>
  onValueChange: (key: string, value: string) => void
  handleError: (error: boolean) => void
  onSupplementalParamsChange?: (key: string, params: Record<string, string>) => void
}

// @dev supports front-end editing of PMPs by the artist, token owner, and valid delegate.xyz v2 delegates
// TODO: add support for contract address perms
export const canEditPmp = (
  config: PmpConfigDetailsFragment,
  authorizedAddresses: string[],
  token: TokenDetailsFragment,
  connectedUserAddress?: string
): boolean => {
  const isTimeLocked = config.pmp_locked_after_timestamp
    ? new Date(config.pmp_locked_after_timestamp) <= new Date()
    : false
  if (isTimeLocked) return false

  const artistAddress = token.project.artist_address?.toLowerCase()
  const tokenOwnerAddress = token.owner_address?.toLowerCase()
  const normalizedAuthorizedAddresses = authorizedAddresses.map((addr) => addr.toLowerCase())
  const normalizedConnectedUserAddress = connectedUserAddress?.toLowerCase()

  // Artist permissions only apply to the directly connected wallet, not delegates
  const isArtist = artistAddress && normalizedConnectedUserAddress === artistAddress
  // For token owner permissions, check if the token owner address is in the authorized addresses
  // This works because authorizedAddresses includes both the connected wallet and any vault addresses
  // that the connected wallet is a delegate for
  const isTokenOwnerOrDelegate = tokenOwnerAddress && normalizedAuthorizedAddresses.includes(tokenOwnerAddress)

  switch (config.auth_option) {
    case 'Address':
      return false
    case 'Artist':
      return isArtist || false
    case 'TokenOwner':
      return isTokenOwnerOrDelegate || false
    case 'ArtistAndAddress':
      return isArtist || false
    case 'ArtistAndTokenOwner':
      return isArtist || isTokenOwnerOrDelegate || false
    case 'TokenOwnerAndAddress':
      return isTokenOwnerOrDelegate || false
    case 'ArtistAndTokenOwnerAndAddress':
      return isArtist || isTokenOwnerOrDelegate || false
    default:
      return false
  }
}

const createPmpSchema = (config: PmpConfigDetailsFragment) => {
  switch (config.param_type) {
    case Pmp_Param_Types_Enum.HexColor:
      return z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid hex color (e.g. #FF0000)')

    case Pmp_Param_Types_Enum.Uint256Range:
      return z.string().refine(
        (val) => {
          try {
            const num = BigInt(val)
            if (config.min_range && num < BigInt(config.min_range)) {
              return false
            }
            if (config.max_range && num > BigInt(config.max_range)) {
              return false
            }
            return true
          } catch {
            return false
          }
        },
        `Must be a valid number between ${BigInt(config.min_range ?? '').toString()} and ${BigInt(config.max_range ?? '').toString()}`
      )

    case Pmp_Param_Types_Enum.Int256Range:
      return z.string().refine(
        (val) => {
          try {
            const num = BigInt(val)
            // Convert to signed integer if it's a negative number in two's complement
            const signedNum = num >= BIT_255 ? num - BIT_256 : num

            if (config.min_range) {
              const minSigned =
                BigInt(config.min_range) >= BIT_255 ? BigInt(config.min_range) - BIT_256 : BigInt(config.min_range)
              if (signedNum < minSigned) {
                return false
              }
            }
            if (config.max_range) {
              const maxSigned =
                BigInt(config.max_range) >= BIT_255 ? BigInt(config.max_range) - BIT_256 : BigInt(config.max_range)
              if (signedNum > maxSigned) {
                return false
              }
            }
            return true
          } catch {
            return false
          }
        },
        `Must be a valid number between ${(() => {
          const min = config.min_range
            ? BigInt(config.min_range) >= BIT_255
              ? BigInt(config.min_range) - BIT_256
              : BigInt(config.min_range)
            : undefined
          const max = config.max_range
            ? BigInt(config.max_range) >= BIT_255
              ? BigInt(config.max_range) - BIT_256
              : BigInt(config.max_range)
            : undefined
          return `${min?.toString() ?? '-∞'} and ${max?.toString() ?? '∞'}`
        })()}`
      )

    case Pmp_Param_Types_Enum.DecimalRange:
      return z.string().refine(
        (val) => {
          try {
            const num = Number(val)
            if (isNaN(num)) return false
            if (config.min_range && num < Number(config.min_range) / 1e10) {
              return false
            }
            if (config.max_range && num > Number(config.max_range) / 1e10) {
              return false
            }
            return true
          } catch {
            return false
          }
        },
        `Must be a valid decimal between ${Number(config.min_range) / 1e10} and ${Number(config.max_range) / 1e10}`
      )

    case Pmp_Param_Types_Enum.Bool:
      return z.enum(['true', 'false'])

    case Pmp_Param_Types_Enum.Timestamp:
      return z.string().refine(
        (val) => {
          try {
            const timestamp = BigInt(val)
            if (config.min_range && timestamp < BigInt(config.min_range)) {
              return false
            }
            if (config.max_range && timestamp > BigInt(config.max_range)) {
              return false
            }
            return true
          } catch {
            return false
          }
        },
        `Must be a valid timestamp between ${new Date(Number(config.min_range) * 1000).toLocaleString()} and ${new Date(Number(config.max_range) * 1000).toLocaleString()}`
      )

    case Pmp_Param_Types_Enum.Select:
      if (!config.select_options?.length) return z.string()
      return z.enum(config.select_options as [string, ...string[]])

    default:
      return z.string()
  }
}

function InputControls({
  localValue,
  maxRange,
  minRange,
  step,
  handleValueChange
}: {
  localValue: string
  maxRange: string | number | undefined
  minRange: string | number | undefined
  step: number
  handleValueChange: (value: string) => void
}) {
  return (
    <div className="right-xs absolute top-1/2 flex -translate-y-1/2 flex-col">
      <button
        type="button"
        className="text-on-surface-inverted hover:text-on-surface-inverted-hover"
        onClick={() => {
          const current = Number(localValue)
          const max = maxRange ? Number(maxRange) : Infinity
          const newValue = Math.min(current + step, max)
          handleValueChange(step === 1 ? newValue.toString() : newValue.toFixed(2))
        }}
      >
        <LeftCaretIcon className="fill-on-surface-inverted-variant h-[14px] w-[14px] rotate-90" />
      </button>
      <button
        type="button"
        className="text-on-surface-inverted hover:text-on-surface-inverted-hover"
        onClick={() => {
          const current = Number(localValue)
          const min = minRange ? Number(minRange) : -Infinity
          const newValue = Math.max(current - step, min)
          handleValueChange(step === 1 ? newValue.toString() : newValue.toFixed(2))
        }}
      >
        <LeftCaretIcon className="fill-on-surface-inverted-variant h-[14px] w-[14px] -rotate-90" />
      </button>
    </div>
  )
}

function PmpField({
  config,
  activePmp,
  authorizedAddresses,
  connectedUserAddress,
  token,
  customizationParams,
  onValueChange,
  handleError,
  onSupplementalParamsChange
}: PmpFieldProps) {
  const [error, setError] = useState<string | null>(null)

  // Get potentially overridden config
  const {
    config: effectiveConfig,
    isLoading: configIsLoading,
    error: configError,
    conditionData,
    supplementalParams
  } = usePmpConfigWithOverride(config, { token, userAddress: connectedUserAddress, customizationParams })

  // Report supplemental params changes
  useEffect(() => {
    if (supplementalParams && Object.keys(supplementalParams).length > 0 && onSupplementalParamsChange) {
      onSupplementalParamsChange(config.key, supplementalParams)
    } else if (!supplementalParams && onSupplementalParamsChange) {
      // Clear supplemental params when none are provided
      onSupplementalParamsChange(config.key, {})
    }
  }, [supplementalParams, config.key, onSupplementalParamsChange])

  const schema = useMemo(() => createPmpSchema(effectiveConfig), [effectiveConfig])

  const isEditable = useMemo(() => {
    return canEditPmp(effectiveConfig, authorizedAddresses, token, connectedUserAddress)
  }, [effectiveConfig, authorizedAddresses, token, connectedUserAddress])

  const currentValue =
    customizationParams[config.key] ?? activePmp?.configured_value ?? activePmp?.artist_configured_value_string ?? ''

  const [localValue, setLocalValue] = useState<string>(currentValue ?? '')

  const handleValueChange = (value: string) => {
    setLocalValue(value)
    setError(null)
    handleError(false)
    try {
      schema.parse(value)
      onValueChange(config.key, value)
    } catch (err) {
      if (err instanceof z.ZodError) {
        setError(err.errors[0].message)
        handleError(true)
      }
    }
  }

  // Handle configuration loading and errors
  if (configIsLoading) {
    return (
      <div className="bg-surface-container-inverted p-xs rounded-xs mb-sm">
        <div className="mb-xs rounded-xs flex items-center justify-between">
          <DetailText className="text-on-surface-inverted-variant font-medium uppercase">{config.key}</DetailText>
          <LoadingSpinner className="h-sm w-sm fill-on-surface-inverted-variant" />
        </div>
        <BodyText className="text-on-surface-inverted-variant text-xs">Loading configuration...</BodyText>
      </div>
    )
  }

  if (configError) {
    return (
      <div className="bg-surface-container-inverted p-xs rounded-xs mb-sm">
        <div className="mb-xs rounded-xs flex items-center justify-between">
          <DetailText className="text-on-surface-inverted-variant font-medium uppercase">{config.key}</DetailText>
        </div>
        <BodyText className="text-error-inverted text-xs">Failed to load configuration</BodyText>
      </div>
    )
  }

  const handleDateChange = (date: any) => {
    if (!date) {
      handleValueChange('')
      return
    }
    const timestamp = convertDateValueToString(date)
    handleValueChange(timestamp)
  }

  const renderField = () => {
    const inputClassName = cn(
      'bg-surface-inverted text-on-surface-inverted border-outline-inverted rounded-xs appearance-none',
      {
        'border-error-inverted': error
      }
    )

    switch (effectiveConfig.param_type) {
      case Pmp_Param_Types_Enum.HexColor:
        return (
          <div className="w-full">
            <ColorPicker
              value={localValue}
              onChange={handleValueChange}
              className={inputClassName}
              hexPreviewClassName={
                '!p-0 rounded-xs border-primary-inverted h-[24px] w-[24px] min-w-[24px] min-h-[24px] inline-block border'
              }
            />
          </div>
        )
      case Pmp_Param_Types_Enum.String:
        return (
          <Input
            className={inputClassName}
            type="text"
            value={localValue}
            onChange={(e) => handleValueChange(e.target.value)}
          />
        )
      case Pmp_Param_Types_Enum.Uint256Range: {
        const minRange = effectiveConfig.min_range ? BigInt(effectiveConfig.min_range).toString() : undefined
        const maxRange = effectiveConfig.max_range ? BigInt(effectiveConfig.max_range).toString() : undefined

        return (
          <div className="relative">
            <Input
              type="number"
              className={inputClassName}
              step="1"
              min={minRange}
              max={maxRange}
              value={localValue}
              onChange={(e) => handleValueChange(e.target.value)}
            />
            <InputControls
              localValue={localValue}
              maxRange={maxRange}
              minRange={minRange}
              step={1}
              handleValueChange={handleValueChange}
            />
          </div>
        )
      }
      case Pmp_Param_Types_Enum.Int256Range: {
        let minRange
        let unsignedMinRange
        let maxRange
        let unsignedMaxRange
        if (effectiveConfig.min_range) {
          unsignedMinRange = BigInt(effectiveConfig.min_range)
          minRange = Number(unsignedMinRange >= BIT_255 ? unsignedMinRange - BIT_256 : unsignedMinRange).toString()
        }
        if (effectiveConfig.max_range) {
          unsignedMaxRange = BigInt(effectiveConfig.max_range)
          maxRange = Number(unsignedMaxRange >= BIT_255 ? unsignedMaxRange - BIT_256 : unsignedMaxRange).toString()
        }

        return (
          <div className="relative">
            <Input
              type="number"
              className={inputClassName}
              step="1"
              min={minRange}
              max={maxRange}
              value={localValue}
              onChange={(e) => handleValueChange(e.target.value)}
            />
            <InputControls
              localValue={localValue}
              minRange={minRange}
              maxRange={maxRange}
              step={1}
              handleValueChange={handleValueChange}
            />
          </div>
        )
      }
      case Pmp_Param_Types_Enum.DecimalRange: {
        let minDecimal
        let minRange
        let maxDecimal
        let maxRange
        if (effectiveConfig.min_range) {
          minDecimal = String(BigInt(effectiveConfig.min_range) % BigInt(1e10))
            .padStart(10, '0')
            .slice(0, 4)
          minRange = `${BigInt(effectiveConfig.min_range) / BigInt(1e10)}.${minDecimal}`
        }

        if (effectiveConfig.max_range) {
          maxDecimal = String(BigInt(effectiveConfig.max_range) % BigInt(1e10))
            .padStart(10, '0')
            .slice(0, 4)
          maxRange = `${BigInt(effectiveConfig.max_range) / BigInt(1e10)}.${maxDecimal}`
        }

        return (
          <div className="relative">
            <Input
              className={inputClassName}
              type="number"
              step="0.01"
              min={minRange ? Number(minRange) : undefined}
              max={maxRange ? Number(maxRange) : undefined}
              value={localValue}
              onChange={(e) => handleValueChange(e.target.value)}
            />
            <InputControls
              localValue={localValue}
              minRange={minRange}
              maxRange={maxRange}
              step={0.01}
              handleValueChange={handleValueChange}
            />
          </div>
        )
      }
      case Pmp_Param_Types_Enum.Bool:
        return (
          <SwitchToggle
            leftLabel=""
            switchedRight={localValue === 'true'}
            onSwitchChange={(checked) => handleValueChange(checked.toString())}
            variant="inverted"
            className="w-auto"
          />
        )
      case Pmp_Param_Types_Enum.Timestamp: {
        let minDate
        let maxDate
        // @dev the date picker only supports years up to 9999
        // maximum timestamp for year 9999 (253402300799)
        const MAX_TIMESTAMP = BigInt('253402300799')

        if (effectiveConfig.min_range) {
          const minTimestamp = BigInt(effectiveConfig.min_range)
          minDate = formatDateForPicker(minTimestamp.toString())
        }
        if (effectiveConfig.max_range) {
          const maxTimestamp = BigInt(effectiveConfig.max_range)
          if (maxTimestamp <= MAX_TIMESTAMP) {
            maxDate = formatDateForPicker(maxTimestamp.toString())
          }
        }
        return (
          <DateTimePicker
            id={config.key}
            className={inputClassName}
            granularity="minute"
            value={localValue ? (formatDateForPicker(localValue) as any) : null}
            onChange={handleDateChange}
            variant="inverted"
            minValue={minDate ? (minDate as unknown as DateValue) : undefined}
            maxValue={maxDate ? (maxDate as unknown as DateValue) : undefined}
          />
        )
      }
      case Pmp_Param_Types_Enum.Select: {
        // Use condition data for enhanced labels if available
        const selectOptions = effectiveConfig.select_options ?? []
        const optionsWithLabels: GridSelectorOption[] =
          conditionData && Array.isArray(conditionData)
            ? conditionData.map((item: any) => ({
                value: item.value,
                label: item.label || item.value
              }))
            : selectOptions.map((option) => ({ value: option, label: option }))

        return (
          <GridSelector
            options={optionsWithLabels}
            selected={localValue ?? optionsWithLabels[0]?.value ?? ''}
            setSelected={(option: string) => handleValueChange(option)}
          />
        )
      }
      default:
        return (
          <Input
            className={inputClassName}
            type="text"
            value={localValue}
            onChange={(e) => handleValueChange(e.target.value)}
          />
        )
    }
  }

  const renderWithError = (field: JSX.Element) => (
    <div className="gap-2xs flex flex-col">
      {field}
      {error && <DetailText className="text-error-inverted text-xs">{error}</DetailText>}
    </div>
  )

  return (
    <div className="bg-surface-container-inverted p-xs rounded-xs mb-sm">
      <div className="mb-xs rounded-xs flex items-center justify-between">
        <DetailText
          className={`font-medium uppercase ${isEditable ? 'text-on-surface-inverted' : 'text-on-surface-inverted-variant'}`}
        >
          {effectiveConfig.key}
        </DetailText>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              {isEditable ? (
                <InfoCircleIcon className="w-sm h-sm fill-on-surface-inverted-variant cursor-pointer" />
              ) : (
                <LockIcon className="w-sm h-sm fill-on-surface-inverted-variant cursor-pointer" />
              )}
            </div>
          </TooltipTrigger>
          <TooltipPortal>
            <TooltipContent className="bg-surface-container-inverted p-md border-0 shadow-[4px_4px_8px_-2px_rgba(0,0,0,0.1)]">
              <PmpCustomizableDetails pmpDetails={effectiveConfig as Pmp_Configs} colorTheme="primary-inverted" />
            </TooltipContent>
          </TooltipPortal>
        </Tooltip>
      </div>
      {isEditable && <div>{renderWithError(renderField())}</div>}
    </div>
  )
}

type TokenPmpCustomizationProps = {
  token: TokenDetailsFragment
  modal: boolean
  connectedUserAddress?: string
  authorizedAddresses: string[]
  handleIsCustomizing: (isCustomizing: boolean) => void
  onCustomizationChange?: (key: string, value: string) => void
  onSupplementalParamsChange?: (params: Record<string, string>) => void
}

export function TokenPmpCustomization({
  token,
  modal,
  connectedUserAddress,
  authorizedAddresses,
  handleIsCustomizing,
  onCustomizationChange,
  onSupplementalParamsChange
}: TokenPmpCustomizationProps): React.ReactElement {
  const walletClient = useAuthenticatedWalletClient()
  const { data: hash, writeContract } = useWriteContract()
  const { isLoading: isConfirming, isSuccess: isConfirmed } = useWaitForTransactionReceipt({
    hash
  })
  const router = useRouter()
  const [pendingTransaction, setPendingTransaction] = useState<{
    pmpInputs: any[]
    updatedAtBefore: string
  } | null>(null)

  // watch for transaction confirmation
  useEffect(() => {
    if (isConfirmed && pendingTransaction) {
      const { pmpInputs, updatedAtBefore } = pendingTransaction
      setIsSyncing(true)

      // poll hasura for synced pmp update
      const pmpKey = pmpInputs?.[0]?.key
      if (!pmpKey) return

      pollPmpsMetadata(pmpKey, token.id, updatedAtBefore)
        .then(() => {
          // revalidate the token page
          router.refresh()
          setHasSyncingCompleted(true)
        })
        .catch((error) => {
          console.error('Error polling PMP metadata:', error)
        })
        .finally(() => {
          setIsSyncing(false)
          setPendingTransaction(null)
        })
    }
  }, [isConfirmed, pendingTransaction, token.id, router])

  useNavBar({
    border: !modal
  })

  const project = token.project
  const tokenName = `${project.name ?? ''} #${token.invocation}`
  const tokenId = token.id
  const coreContractAddress = tokenId.split('-')[0] as `0x${string}`
  const shortTokenId = BigInt(tokenId.split('-')[1])
  const projectPmpConfig = token.project.pmp_project_configs?.[0]?.pmp_configs
  const currentPmpContractAddress = token.project.pmp_project_configs?.[0]?.pmp_address

  const [formHasError, setFormHasError] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [hasSyncingCompleted, setHasSyncingCompleted] = useState(false)

  // format initial customization params
  const initialCustomizationParams = useMemo(() => {
    const params: Record<string, string> = {}
    token.active_pmps?.forEach((pmp) => {
      const config = projectPmpConfig?.find((c) => c.key === pmp.key)

      const value =
        pmp.configured_value || pmp.non_artist_configured_value_string || pmp.artist_configured_value_string || ''

      let formattedValue = value
      if (config) {
        const formatted = formatConfiguredPMPValue(pmp, config as Pmp_Configs)
        formattedValue = formatted === null ? value : String(formatted)
      }
      params[pmp.key] = formattedValue
    })
    return params
  }, [token.active_pmps, projectPmpConfig])

  // initialize state with a deep copy of initialCustomizationParams
  const [customizationParams, setCustomizationParams] = useState<Record<string, string>>(() => ({
    ...initialCustomizationParams
  }))
  const [formKey, setFormKey] = useState(0)

  const handleValueChange = useCallback(
    (key: string, value: string) => {
      const newValue = value || ''
      setCustomizationParams((prev) => ({
        ...prev,
        [key]: newValue
      }))
      onCustomizationChange?.(key, newValue)
    },
    [onCustomizationChange]
  )

  const handlePmpSupplementalParamsChange = useCallback(
    (key: string, params: Record<string, string>) => {
      onSupplementalParamsChange?.(params)
    },
    [onSupplementalParamsChange]
  )

  const handleSave = async () => {
    if (!walletClient || !currentPmpContractAddress) return

    try {
      const pmpInputs = projectPmpConfig
        .map((config) => {
          const newValue = customizationParams[config.key]
          const activePmp = token.active_pmps?.find((pmp) => pmp.key === config.key)
          // Skip if empty
          if (!newValue) {
            return null
          }

          // Check if user is artist for configuringArtistString
          const isArtist = token.project.artist_address?.toLowerCase() === connectedUserAddress?.toLowerCase()
          const isStringType = config.param_type === Pmp_Param_Types_Enum.String

          const { paramType, configuredValue } = formatPmpTokenParamsInput(newValue, config as Pmp_Configs)
          // skip if no change
          if (
            (isStringType &&
              newValue ===
                (activePmp?.artist_configured_value_string || activePmp?.non_artist_configured_value_string)) ||
            (!isStringType && configuredValue === activePmp?.configured_value)
          ) {
            return null
          }

          return {
            key: config.key,
            configuredParamType: paramType,
            configuredValue,
            configuringArtistString: isArtist && isStringType,
            configuredValueString: isStringType ? newValue : ''
          }
        })
        .filter((input): input is NonNullable<typeof input> => input !== null)

      if (!pmpInputs?.length) return

      trackButtonClicked(TrackedButtonNames.savePmpCustomization, {
        token,
        customProperties: {
          numUpdates: pmpInputs.length
        }
      })

      const updatedAtBefore = new Date().toISOString()

      try {
        writeContract({
          abi: IPMPV0ABI,
          address: currentPmpContractAddress as `0x${string}`,
          functionName: 'configureTokenParams',
          args: [coreContractAddress, shortTokenId, pmpInputs]
        })
        setPendingTransaction({ pmpInputs, updatedAtBefore })
      } catch (error) {
        console.error('Transaction error details:', {
          error,
          message: error.message,
          code: error.code,
          data: error.data,
          cause: error.cause
        })
        throw error
      }
    } catch (error) {
      console.error('Error saving PMP customizations:', error)
      setIsSyncing(false)
    }
  }

  const handleUndo = () => {
    // reset fields
    const resetParams = Object.fromEntries(
      Object.entries(initialCustomizationParams).map(([key, value]) => [key, value || ''])
    )
    setCustomizationParams(resetParams)

    // re-render form components
    setFormKey((prev) => prev + 1)

    // reset live view preview
    Object.entries(resetParams).forEach(([key, value]) => {
      onCustomizationChange?.(key, value || '')
    })

    trackButtonClicked(TrackedButtonNames.undoPmpCustomization, {
      token
    })
  }

  // Update save button disabled state to compare with initial values
  const hasChanges = useMemo(() => {
    return Object.entries(customizationParams).some(([key, value]) => {
      const initialValue = initialCustomizationParams[key]
      return value !== initialValue
    })
  }, [customizationParams, initialCustomizationParams])

  const renderContent = () => {
    if (isSyncing || isConfirming) {
      return (
        <div className="px-sm flex h-full flex-col items-start justify-center">
          <LoadingSpinner className="h-md w-md mb-sm fill-on-surface-inverted" />
          <BodyText className="text-on-surface-variant">Finalizing customization</BodyText>
          <HeadlineText className="mb-sm text-on-surface-inverted font-medium">{tokenName}</HeadlineText>
          <BodyText className="text-on-surface-inverted">
            Your changes are being processed. You can leave this screen at anytime.
          </BodyText>
        </div>
      )
    }

    if (hasSyncingCompleted) {
      return (
        <div className="px-sm flex h-full flex-col items-start justify-center">
          <BodyText className="text-on-surface-variant">Success!</BodyText>
          <HeadlineText className="mb-sm text-on-surface-inverted font-medium">{tokenName}</HeadlineText>
          <BodyText className="text-on-surface-inverted">
            Your changes have been saved. You may return to the artwork page or customize your artwork again. Check back
            in a few minutes to see updates reflected in the Attributes and Provenance tabs.
          </BodyText>
          <Button
            className="mt-md w-full"
            colorTheme="inverted-primary"
            branded
            onClick={() => handleIsCustomizing(false)}
          >
            Back to artwork page
          </Button>
          <Button
            className="mt-xs w-full"
            colorTheme="inverted-secondary"
            branded
            onClick={() => {
              setHasSyncingCompleted(false)
            }}
          >
            Customize again
          </Button>
        </div>
      )
    }

    return (
      <div className="flex h-full flex-col">
        <div className="overflow-y-auto">
          <div className="px-lg">
            <Button
              colorTheme="icon"
              branded
              className="mb-sm flex-1 !p-0"
              onClick={() => handleIsCustomizing(false)}
              iconLeft={<LeftArrowIcon className="fill-on-surface-variant mr-2xs h-[14px] w-[14px]" />}
            >
              <BodyText className="text-on-surface-variant text-xs">Back</BodyText>
            </Button>
            <BodyText className="text-on-surface-variant mb-2xs">Customizing artwork</BodyText>
            <HeadlineText className="mb-sm text-on-surface-inverted font-medium">{tokenName}</HeadlineText>
            <BodyText className="text-on-surface-inverted mb-xs">
              Be part of this artwork&apos;s evolution through your own customization selections.
            </BodyText>
            <BodyText className="text-on-surface-variant text-xs">
              {(() => {
                const editablePmps = projectPmpConfig
                  ?.filter((config) => canEditPmp(config, authorizedAddresses, token, connectedUserAddress))
                  .map((config) => config.key)

                if (!editablePmps?.length) {
                  return "You don't have permission to customize these params."
                }

                return `You have permission to customize: ${editablePmps.join(', ')}.`
              })()}
            </BodyText>
            <BodyText className="text-on-surface-variant text-xs">Learn more about customizable artwork here.</BodyText>
          </div>

          <div className="mt-md px-lg flex-1">
            {projectPmpConfig
              ?.sort((a, b) => {
                const aEditable = canEditPmp(a, authorizedAddresses, token, connectedUserAddress)
                const bEditable = canEditPmp(b, authorizedAddresses, token, connectedUserAddress)
                if (aEditable === bEditable) return 0
                return aEditable ? -1 : 1
              })
              .map((config) => {
                const activePmp = token.active_pmps?.find((pmp) => pmp.key === config.key)

                return (
                  <PmpField
                    key={`${config.key}-${formKey}`}
                    config={config}
                    activePmp={activePmp}
                    authorizedAddresses={authorizedAddresses}
                    connectedUserAddress={connectedUserAddress}
                    token={token}
                    customizationParams={customizationParams}
                    onValueChange={handleValueChange}
                    handleError={setFormHasError}
                    onSupplementalParamsChange={handlePmpSupplementalParamsChange}
                  />
                )
              })}
          </div>
        </div>
        <BodyText className="text-on-surface-variant mt-sm text-center text-xs">
          Your customizations will be permanently recorded on-chain.
        </BodyText>

        <div className="gap-sm p-lg bottom-0 flex w-full md:sticky">
          <Button
            colorTheme="inverted-secondary"
            branded
            onClick={handleUndo}
            disabled={isSyncing || !hasChanges}
            className="flex-1"
          >
            Undo
          </Button>
          <Button
            colorTheme="inverted-primary"
            branded
            onClick={handleSave}
            disabled={!hasChanges || !walletClient || formHasError}
            className="flex-1"
          >
            Submit
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'gap-sm bg-surface-inverted pt-lg left-0 top-0 z-10 flex flex-col [--closed-info-x:0%] md:absolute md:h-full md:w-[500px] md:overflow-hidden md:[--closed-info-x:-100%]'
      )}
    >
      {renderContent()}
    </div>
  )
}
